import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  getDoc,
  startAfter,
  limit,
  Timestamp
  } from 'firebase/firestore';
import { db } from './firebase';

export const customerService = {
  // Gerar código único de indicação
  generateReferralCode(name, document) {
    // Usar as primeiras 3 letras do nome + 4 últimos dígitos do CPF/CNPJ + timestamp
    const namePrefix = name.replace(/[^a-zA-Z]/g, '').substring(0, 3).toUpperCase();
    const documentSuffix = document.replace(/[^\d]/g, '').slice(-4);
    const timestamp = Date.now().toString().slice(-4);
    return `${namePrefix}${documentSuffix}${timestamp}`;
  },

  // Verificar se código de indicação existe
  async checkReferralCode(referralCode, companyId) {
    try {
      const customersRef = collection(db, 'customers');
      const q = query(
        customersRef, 
        where('referralCode', '==', referralCode),
        where('companyId', '==', companyId)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))[0] || null;
    } catch (error) {
      return null;
    }
  },

  // Processar indicação (dar pontos para indicador e indicado)
  async processReferral(referredCustomerId, referrerCustomerId, companyId, referralSettings) {
    try {
      const { pointsService } = await import('./pointsService');
      
      // Dar pontos para o novo cliente (indicado)
      if (referralSettings.referredPoints > 0) {
        await pointsService.addPoints({
          customerId: referredCustomerId,
          companyId: companyId,
          points: Math.abs(referralSettings.referredPoints), // Garantir que seja positivo
          type: 'referral_bonus',
          description: `Bônus por ser indicado: ${Math.abs(referralSettings.referredPoints)} pontos`,
          employeeName: 'Sistema (Indicação)'
        });
      }

      // Dar pontos para quem indicou
      if (referralSettings.referrerPoints > 0) {
        await pointsService.addPoints({
          customerId: referrerCustomerId,
          companyId: companyId,
          points: Math.abs(referralSettings.referrerPoints), // Garantir que seja positivo
          type: 'referral_bonus',
          description: `Bônus por indicação: ${Math.abs(referralSettings.referrerPoints)} pontos`,
          employeeName: 'Sistema (Indicação)'
        });
      }

      // Atualizar contador de indicações do referenciador
      const referrerRef = doc(db, 'customers', referrerCustomerId);
      const referrerDoc = await getDoc(referrerRef);
      const currentReferrals = referrerDoc.data()?.totalReferrals || 0;
      
      await updateDoc(referrerRef, {
        totalReferrals: currentReferrals + 1,
        updatedAt: new Date().toISOString()
      });

      return true;
    } catch (error) {
      throw new Error('Erro ao processar indicação');
    }
  },

  // Listar todos os clientes (admin)
  async getAll() {
    try {
      const customersRef = collection(db, 'customers');
      const q = query(customersRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      throw new Error('Erro ao buscar todos os clientes');
    }
  },

  // Listar clientes da empresa
  async getByCompany(companyId) {
    try {
      if (!companyId) {
        return [];
      }
      
      if (typeof companyId !== 'string') {
        return [];
      }
      
      const customersRef = collection(db, 'customers');
      const q = query(
        customersRef, 
        where('companyId', '==', companyId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const customers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Calcular pontos reais para cada cliente
      const customersWithRealPoints = await Promise.all(
        customers.map(async (customer) => {
          try {
            // Buscar histórico de pontos do cliente
            const pointsHistory = await this.getCustomerPointsHistory(customer.id);
            
            // Calcular pontos atuais baseado no histórico
            const currentPoints = pointsHistory.reduce((total, transaction) => {
              // Tipos que adicionam pontos (valores positivos)
              const addTypes = ['add', 'purchase', 'bonus', 'referral_bonus', 'welcome_bonus', 'manual'];
              // Tipos que removem pontos (valores negativos)
              const redeemTypes = ['redeem', 'redemption', 'expired'];
              
              if (transaction.points > 0 && addTypes.includes(transaction.type)) {
                return total + transaction.points;
              } else if (transaction.points > 0 && redeemTypes.includes(transaction.type)) {
                return total - transaction.points;
              } else if (transaction.points < 0) {
                // Se os pontos já são negativos, adicionar ao total (pois são negativos)
                return total + transaction.points;
              }
              return total;
            }, 0);
            
            return {
              ...customer,
              points: Math.max(0, currentPoints) // Garantir que não seja negativo
            };
          } catch (error) {
            console.warn(`Erro ao calcular pontos do cliente ${customer.id}:`, error);
            return customer; // Retornar cliente com pontos originais em caso de erro
          }
        })
      );
      
      return customersWithRealPoints;
    } catch (error) {
      throw new Error('Erro ao buscar clientes da empresa: ' + error.message);
    }
  },

  // Função auxiliar para buscar histórico de pontos do cliente
  async getCustomerPointsHistory(customerId) {
    try {
      // Tentar usar o pointsService que já tem as permissões corretas
      try {
        const { pointsService } = await import('./pointsService');
        const pointsHistory = await pointsService.getByCustomer(customerId);
        return pointsHistory;
      } catch (pointsServiceError) {
        console.warn('Erro ao usar pointsService, tentando query direta:', pointsServiceError);
        
        // Fallback para query direta
        const pointsRef = collection(db, 'points_history');
        const q = query(
          pointsRef,
          where('customerId', '==', customerId)
        );
        const snapshot = await getDocs(q);
        const results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        return results;
      }
    } catch (error) {
      console.error(`Erro detalhado ao buscar histórico de pontos do cliente ${customerId}:`, error);
      return [];
    }
  },

  // Atualizar clientes existentes com códigos de indicação
  async updateExistingCustomersWithReferralCodes() {
    try {
      // Buscar todos os clientes que não possuem referralCode ou têm valor null/undefined
      const customersRef = collection(db, 'customers');
      const snapshot = await getDocs(customersRef);
      
      let updatedCount = 0;
      const updatePromises = [];
      
      snapshot.forEach((docSnapshot) => {
        const customer = docSnapshot.data();
        
        // Verificar se o cliente não tem referralCode ou está vazio
        if (!customer.referralCode) {
          const referralCode = this.generateReferralCode(customer.name, customer.cpf);
          
          const updatePromise = updateDoc(docSnapshot.ref, {
            referralCode: referralCode,
            totalReferrals: customer.totalReferrals || 0,
            updatedAt: new Date().toISOString()
          });
          
          updatePromises.push(updatePromise);
          updatedCount++;
        }
      });
      
      if (updatePromises.length > 0) {
        await Promise.all(updatePromises);
      }
      
      return updatedCount;
    } catch (error) {
      throw new Error('Erro ao atualizar clientes existentes');
    }
  },

  // Buscar cliente por CPF ou CNPJ (consulta pública)
  async getByDocument(document) {
    try {
      if (!document || typeof document !== 'string') {
        throw new Error('CPF/CNPJ é obrigatório e deve ser uma string');
      }

      // Limpar documento para garantir que esteja no formato correto
      const cleanDocument = document.replace(/[^\d]/g, '');
      
      if (cleanDocument.length === 0) {
        throw new Error('CPF/CNPJ não pode estar vazio');
      }
      
      if (cleanDocument.length !== 11 && cleanDocument.length !== 14) {
        throw new Error(`CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos. Você digitou ${cleanDocument.length} dígitos: ${cleanDocument}`);
      }

      const customersRef = collection(db, 'customers');
      const q = query(customersRef, where('cpf', '==', cleanDocument));
      const snapshot = await getDocs(q);
      
      const customers = [];
      snapshot.forEach((doc) => {
        customers.push({ id: doc.id, ...doc.data() });
      });
      
      // Calcular pontos reais para cada cliente
      const customersWithRealPoints = await Promise.all(
        customers.map(async (customer) => {
          try {
            // Buscar histórico de pontos do cliente
            const pointsHistory = await this.getCustomerPointsHistory(customer.id);
            
            // Calcular pontos atuais baseado no histórico
            const currentPoints = pointsHistory.reduce((total, transaction) => {
              // Tipos que adicionam pontos (valores positivos)
              const addTypes = ['add', 'purchase', 'bonus', 'referral_bonus', 'welcome_bonus', 'manual'];
              // Tipos que removem pontos (valores negativos)
              const redeemTypes = ['redeem', 'redemption', 'expired'];
              
              if (transaction.points > 0 && addTypes.includes(transaction.type)) {
                return total + transaction.points;
              } else if (transaction.points > 0 && redeemTypes.includes(transaction.type)) {
                return total - transaction.points;
              } else if (transaction.points < 0) {
                // Se os pontos já são negativos, adicionar ao total (pois são negativos)
                return total + transaction.points;
              }
              return total;
            }, 0);
            
            return {
              ...customer,
              points: Math.max(0, currentPoints) // Garantir que não seja negativo
            };
          } catch (error) {
            console.warn(`Erro ao calcular pontos do cliente ${customer.id}:`, error);
            return customer; // Retornar cliente com pontos originais em caso de erro
          }
        })
      );
      
      return customersWithRealPoints;
    } catch (error) {
      throw new Error(`Erro ao buscar cliente por CPF/CNPJ: ${error.message}`);
    }
  },

  // Manter compatibilidade com getByCpf (deprecated)
  async getByCpf(cpf) {
    return this.getByDocument(cpf);
  },

  // Buscar cliente por ID
  async getById(customerId) {
    try {
      const customerRef = doc(db, 'customers', customerId);
      const customerSnap = await getDoc(customerRef);
      
      if (customerSnap.exists()) {
        const customer = { id: customerSnap.id, ...customerSnap.data() };
        
        try {
          // Buscar histórico de pontos do cliente
          const pointsHistory = await this.getCustomerPointsHistory(customer.id);
          
          // Calcular pontos atuais baseado no histórico
          const currentPoints = pointsHistory.reduce((total, transaction) => {
            // Tipos que adicionam pontos (valores positivos)
            const addTypes = ['add', 'purchase', 'bonus', 'referral_bonus', 'welcome_bonus', 'manual'];
            // Tipos que removem pontos (valores negativos)
            const redeemTypes = ['redeem', 'redemption', 'expired'];
            
            if (transaction.points > 0 && addTypes.includes(transaction.type)) {
              return total + transaction.points;
            } else if (transaction.points > 0 && redeemTypes.includes(transaction.type)) {
              return total - transaction.points;
            } else if (transaction.points < 0) {
              // Se os pontos já são negativos, adicionar ao total (pois são negativos)
              return total + transaction.points;
            }
            return total;
          }, 0);
          
          return {
            ...customer,
            points: Math.max(0, currentPoints) // Garantir que não seja negativo
          };
        } catch (error) {
          console.warn(`Erro ao calcular pontos do cliente ${customer.id}:`, error);
          return customer; // Retornar cliente com pontos originais em caso de erro
        }
      } else {
        throw new Error('Cliente não encontrado');
      }
    } catch (error) {
      throw error;
    }
  },

  // Criar novo cliente
  async create(customerData) {
    try {
      // Validar dados essenciais
      if (!customerData.name || !customerData.cpf) {
        throw new Error('Nome e CPF/CNPJ são obrigatórios');
      }

      if (!customerData.companyId) {
        throw new Error('ID da empresa é obrigatório');
      }

      // Sanitizar dados de forma mais rigorosa
      const cleanDocument = String(customerData.cpf || '').replace(/[^\d]/g, ''); // Limpar CPF/CNPJ
      const sanitizedData = {
        name: String(customerData.name || '').trim(),
        cpf: cleanDocument, // Campo mantido como "cpf" por compatibilidade, mas aceita CNPJ
        phone: String(customerData.phone || '').replace(/[^\d]/g, ''), // Limpar telefone
        email: customerData.email ? String(customerData.email).trim().toLowerCase() : '',
        emails: customerData.emails && Array.isArray(customerData.emails) ? 
          customerData.emails
            .map(email => String(email).trim().toLowerCase())
            .filter(email => email) : 
          (customerData.email ? [String(customerData.email).trim().toLowerCase()] : []),
        birthDate: customerData.birthDate ? String(customerData.birthDate).trim() : null,
        companyId: String(customerData.companyId || '').trim(),
        points: 0, // Sempre iniciar com 0, pontos serão adicionados via histórico
        referralCode: this.generateReferralCode(customerData.name, cleanDocument), // Código próprio
        referredBy: null, // Será definido se houver indicação válida
        totalReferrals: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Validar campos obrigatórios após limpeza
      const requiredFields = ['name', 'cpf', 'phone', 'companyId'];
      for (const field of requiredFields) {
        if (field === 'phone' || field === 'cpf') {
          // Para telefone e CPF/CNPJ, só verificar se não está vazio (já foram limpos)
          if (!sanitizedData[field] || sanitizedData[field].length === 0) {
            throw new Error(`Campo obrigatório vazio: ${field}`);
          }
        } else {
          // Para outros campos, usar trim
          if (!sanitizedData[field] || sanitizedData[field].trim() === '') {
            throw new Error(`Campo obrigatório vazio: ${field}`);
          }
        }
      }

      // Validar formato do CPF/CNPJ
      if (cleanDocument.length !== 11 && cleanDocument.length !== 14) {
        throw new Error('CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos');
      }

      // Remover campos opcionais vazios
      if (!sanitizedData.birthDate) {
        delete sanitizedData.birthDate;
      }
      if (!sanitizedData.email) {
        delete sanitizedData.email;
      }
      if (!sanitizedData.emails || sanitizedData.emails.length === 0) {
        delete sanitizedData.emails;
      }

      // Verificar se CPF/CNPJ já existe na empresa
      const existingCustomer = await this.getByDocument(sanitizedData.cpf);
      const existingInCompany = existingCustomer.find(c => c.companyId === sanitizedData.companyId);
      if (existingInCompany) {
        throw new Error('CPF/CNPJ já cadastrado para esta empresa');
      }

      // Verificar indicação se foi fornecido código do indicador
      let referrer = null;
      if (customerData.referralCode && customerData.referralCode.trim() !== '') {
        referrer = await this.checkReferralCode(customerData.referralCode.trim(), sanitizedData.companyId);
        if (referrer) {
          sanitizedData.referredBy = customerData.referralCode.trim(); // Salvar código de quem indicou
        }
      }

      const customersRef = collection(db, 'customers');
      
      // Tentar criar o documento
      const docRef = await addDoc(customersRef, sanitizedData);
      
      const customer = {
        id: docRef.id,
        ...sanitizedData,
        birthDate: sanitizedData.birthDate || '' // Garantir que birthDate sempre exista no retorno
      };

      // Buscar configurações da empresa para indicações
      try {
        const { companyService } = await import('./companyService');
        const company = await companyService.getById(sanitizedData.companyId);
        
        // Processar indicação se houver
        if (referrer && company?.referralSettings?.enabled) {
          try {
            await this.processReferral(
              customer.id, 
              referrer.id, 
              sanitizedData.companyId, 
              company.referralSettings
            );
          } catch (referralError) {
            // Não falha o cadastro se apenas a indicação falhar
          }
        }
      } catch (companyError) {
        // Não falha o cadastro se não conseguir buscar dados da empresa
      }
      
      return {
        ...customer,
        referralProcessed: !!referrer
      };
    } catch (error) {
      // Melhor tratamento de erros específicos do Firebase
      if (error.code === 'permission-denied') {
        throw new Error('Erro de permissão: Verifique as regras de segurança do Firestore');
      } else if (error.code === 'invalid-argument') {
        throw new Error('Dados inválidos enviados para o Firebase');
      } else if (error.message.includes('400')) {
        throw new Error('Erro de formato de dados: Verifique se todos os campos estão corretos');
      }
      
      throw new Error(`Erro ao criar cliente: ${error.message}`);
    }
  },

  // Criar novo cliente via cadastro público (com bônus de boas-vindas opcional)
  async createPublic(customerData) {
    try {
      // Importar companyService para buscar dados da empresa
      const { companyService } = await import('./companyService');
      
      // Buscar dados da empresa para pegar o bônus de boas-vindas e configurações de indicação
      const company = await companyService.getById(customerData.companyId);
      
      // Verificar se a empresa tem bônus de boas-vindas configurado
      const welcomeBonus = company?.welcomeBonus || 0;
      const shouldGiveBonus = welcomeBonus > 0;
      
      // Verificar se tem código de indicação válido
      let referrer = null;
      if (customerData.referralCode) {
        referrer = await this.checkReferralCode(customerData.referralCode, customerData.companyId);
        if (!referrer) {
          throw new Error('Código de indicação inválido');
        }
      }
      
      // Usar o método create normal, sem pontos iniciais (serão adicionados pelo histórico)
      const customer = await this.create({
        ...customerData,
        points: 0, // Sempre iniciar com 0, os pontos serão adicionados via histórico
        referredBy: referrer ? referrer.id : null
      });
      
      // Se bônus foi configurado, registrar no histórico de pontos
      if (shouldGiveBonus && welcomeBonus > 0) {
        try {
          const { pointsService } = await import('./pointsService');
          await pointsService.addPoints({
            customerId: customer.id,
            companyId: customerData.companyId,
            points: welcomeBonus,
            type: 'welcome_bonus',
            description: `Bônus de boas-vindas: ${welcomeBonus} pontos`,
            employeeName: 'Sistema (Auto-cadastro)'
          });
        } catch (historyError) {
          // Não falha o cadastro se apenas o histórico falhar
        }
      }
      
      // Processar indicação se houver
      if (referrer && company?.referralSettings?.enabled) {
        try {
          await this.processReferral(
            customer.id, 
            referrer.id, 
            customerData.companyId, 
            company.referralSettings
          );
        } catch (referralError) {
          // Não falha o cadastro se apenas a indicação falhar
        }
      }
      
      return {
        ...customer,
        welcomeBonusGiven: shouldGiveBonus,
        welcomeBonusAmount: welcomeBonus,
        referralProcessed: !!referrer
      };
    } catch (error) {
      throw error;
    }
  },

  // Atualizar cliente
  async update(customerId, customerData) {
    try {
      // Buscar o cliente atual para pegar a empresa
      const currentCustomer = await this.getById(customerId);
      
      // Se está tentando alterar o CPF/CNPJ, verificar se já existe outro cliente com este documento na mesma empresa
      if (customerData.cpf && customerData.cpf !== currentCustomer.cpf) {
        const cleanDocument = String(customerData.cpf).replace(/[^\d]/g, '');
        
        // Validar formato do CPF/CNPJ
        if (cleanDocument.length !== 11 && cleanDocument.length !== 14) {
          throw new Error('CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos');
        }
        
        // Verificar se já existe outro cliente com este documento na mesma empresa
        const existingCustomers = await this.getByDocument(cleanDocument);
        const existingInCompany = existingCustomers.find(c => 
          c.companyId === currentCustomer.companyId && c.id !== customerId
        );
        
        if (existingInCompany) {
          throw new Error('CPF/CNPJ já cadastrado para outro cliente desta empresa');
        }
        
        // Limpar o documento nos dados que serão salvos
        customerData.cpf = cleanDocument;
      }
      
      // Limpar telefone se fornecido
      if (customerData.phone) {
        customerData.phone = String(customerData.phone).replace(/[^\d]/g, '');
      }
      
      // Limpar email se fornecido
      if (customerData.email) {
        customerData.email = String(customerData.email).trim().toLowerCase();
      }
      
      // Processar múltiplos e-mails se fornecido
      if (customerData.emails && Array.isArray(customerData.emails)) {
        customerData.emails = customerData.emails
          .map(email => String(email).trim().toLowerCase())
          .filter(email => email);
          
        // Garantir que o e-mail principal esteja na lista
        if (customerData.email && !customerData.emails.includes(customerData.email)) {
          customerData.emails.unshift(customerData.email);
        }
        
        // Se não há array de e-mails mas há e-mail principal, criar array
      } else if (customerData.email) {
        customerData.emails = [customerData.email];
      }
      
      const customerRef = doc(db, 'customers', customerId);
      await updateDoc(customerRef, {
        ...customerData,
        updatedAt: new Date().toISOString()
      });
      
      return {
        id: customerId,
        ...customerData
      };
    } catch (error) {
      throw new Error(`Erro ao atualizar cliente: ${error.message}`);
    }
  },

  // Deletar cliente
  async delete(customerId) {
    try {
      const customerRef = doc(db, 'customers', customerId);
      await deleteDoc(customerRef);
      return true;
    } catch (error) {
      throw new Error('Erro ao deletar cliente');
    }
  },

  // Atualizar pontos do cliente
  async updatePoints(customerId, newPoints) {
    try {
      const customerRef = doc(db, 'customers', customerId);
      await updateDoc(customerRef, {
        points: newPoints,
        updatedAt: new Date().toISOString()
      });
      
      return { id: customerId, points: newPoints };
    } catch (error) {
      throw new Error('Erro ao atualizar pontos do cliente');
    }
  },

  // Adicionar pontos ao cliente
  async addPoints(customerId, pointsToAdd) {
    try {
      const customerRef = doc(db, 'customers', customerId);
      const customerSnap = await getDoc(customerRef);
      
      if (!customerSnap.exists()) {
        throw new Error('Cliente não encontrado');
      }
      
      const currentPoints = customerSnap.data().points || 0;
      const newPoints = currentPoints + pointsToAdd;
      
      await updateDoc(customerRef, {
        points: newPoints,
        updatedAt: new Date().toISOString()
      });
      
      return { id: customerId, points: newPoints };
    } catch (error) {
      throw new Error('Erro ao adicionar pontos ao cliente');
    }
  },

  // Resgatar pontos do cliente
  async redeemPoints(customerId, pointsToRedeem) {
    try {
      const customerRef = doc(db, 'customers', customerId);
      const customerSnap = await getDoc(customerRef);
      
      if (!customerSnap.exists()) {
        throw new Error('Cliente não encontrado');
      }
      
      const currentPoints = customerSnap.data().points || 0;
      
      if (currentPoints < pointsToRedeem) {
        throw new Error('Pontos insuficientes');
      }
      
      const newPoints = currentPoints - pointsToRedeem;
      
      await updateDoc(customerRef, {
        points: newPoints,
        updatedAt: new Date().toISOString()
      });
      
      return { id: customerId, points: newPoints };
    } catch (error) {
      throw new Error('Erro ao resgatar pontos do cliente');
    }
  },

  // Buscar clientes com paginação e filtros
  async getByCompanyPaginated(companyId, options = {}) {
    try {
      if (!companyId) {
        return { customers: [], totalCount: 0, hasMore: false, lastDoc: null };
      }
      
      const {
        limit: pageLimit = 20,
        startAfter: startAfterDoc = null,
        searchTerm = '',
        sortField = 'createdAt',
        sortDirection = 'desc'
      } = options;

      const customersRef = collection(db, 'customers');
      let q = query(
        customersRef, 
        where('companyId', '==', companyId)
      );

      // Se há termo de busca, fazer busca client-side
      if (searchTerm && searchTerm.trim() !== '') {
        const searchTermLower = searchTerm.toLowerCase().trim();
        const searchNumbers = searchTerm.replace(/\D/g, '');
        
        // Buscar todos os clientes da empresa primeiro
        const allQuery = query(
          customersRef, 
          where('companyId', '==', companyId)
        );
        const allSnapshot = await getDocs(allQuery);
        const allCustomers = allSnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data() 
        }));
        
        // Filtrar no lado cliente
        const filteredCustomers = allCustomers.filter(customer => {
          const nameMatch = customer.name?.toLowerCase().includes(searchTermLower);
          const emailMatch = customer.email?.toLowerCase().includes(searchTermLower);
          const cpfMatch = customer.cpf && searchNumbers && customer.cpf.replace(/\D/g, '').includes(searchNumbers);
          const phoneMatch = customer.phone && searchNumbers && customer.phone.replace(/\D/g, '').includes(searchNumbers);
          
          return nameMatch || emailMatch || cpfMatch || phoneMatch;
        });
        
        // Aplicar ordenação
        filteredCustomers.sort((a, b) => {
          let aValue = a[sortField];
          let bValue = b[sortField];
          
          if (sortField === 'createdAt') {
            aValue = a[sortField]?.toDate?.() || new Date(a[sortField]) || new Date(0);
            bValue = b[sortField]?.toDate?.() || new Date(b[sortField]) || new Date(0);
          }
          
          if (sortField === 'name') {
            aValue = (aValue || '').toLowerCase();
            bValue = (bValue || '').toLowerCase();
          }
          
          if (sortDirection === 'desc') {
            return bValue > aValue ? 1 : -1;
          } else {
            return aValue > bValue ? 1 : -1;
          }
        });
        
        // Aplicar paginação manual
        const startIndex = startAfterDoc ? 
          filteredCustomers.findIndex(c => c.id === startAfterDoc) + 1 : 0;
        const endIndex = startIndex + pageLimit;
        const pageCustomers = filteredCustomers.slice(startIndex, endIndex);
        
        // Calcular pontos reais para clientes filtrados
        const customersWithRealPoints = await Promise.all(
          pageCustomers.map(async (customer) => {
            try {
              const pointsHistory = await this.getCustomerPointsHistory(customer.id);
              const currentPoints = pointsHistory.reduce((total, transaction) => {
                if (transaction.type === 'add' && transaction.points > 0) {
                  return total + transaction.points;
                } else if (transaction.type === 'redeem' || transaction.type === 'expired' || transaction.points < 0) {
                  return total - Math.abs(transaction.points);
                }
                return total;
              }, 0);
              
              return {
                ...customer,
                points: Math.max(0, currentPoints)
              };
            } catch (error) {
              console.warn(`Erro ao calcular pontos do cliente ${customer.id}:`, error);
              return customer;
            }
          })
        );
        
        return {
          customers: customersWithRealPoints,
          totalCount: filteredCustomers.length,
          hasMore: endIndex < filteredCustomers.length,
          lastDoc: pageCustomers[pageCustomers.length - 1]?.id || null
        };
      }

      // Sem filtro de busca, usar paginação nativa do Firestore
      q = query(q, orderBy(sortField, sortDirection));
      
      if (startAfterDoc) {
        q = query(q, startAfter(startAfterDoc));
      }
      
      q = query(q, limit(pageLimit + 1)); // +1 para verificar se há mais páginas
      
      const snapshot = await getDocs(q);
      const customers = snapshot.docs.slice(0, pageLimit).map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      
      // Calcular pontos reais para cada cliente
      const customersWithRealPoints = await Promise.all(
        customers.map(async (customer) => {
          try {
            const pointsHistory = await this.getCustomerPointsHistory(customer.id);
            const currentPoints = pointsHistory.reduce((total, transaction) => {
              if (transaction.type === 'add' && transaction.points > 0) {
                return total + transaction.points;
              } else if (transaction.type === 'redeem' || transaction.type === 'expired' || transaction.points < 0) {
                return total - Math.abs(transaction.points);
              }
              return total;
            }, 0);
            
            return {
              ...customer,
              points: Math.max(0, currentPoints)
            };
          } catch (error) {
            console.warn(`Erro ao calcular pontos do cliente ${customer.id}:`, error);
            return customer;
          }
        })
      );
      
      const hasMore = snapshot.docs.length > pageLimit;
      const lastDoc = customersWithRealPoints[customersWithRealPoints.length - 1];

      return {
        customers: customersWithRealPoints,
        totalCount: customersWithRealPoints.length,
        hasMore,
        lastDoc: lastDoc ? snapshot.docs[customersWithRealPoints.length - 1] : null
      };
    } catch (error) {
      throw new Error('Erro ao buscar clientes: ' + error.message);
    }
  },

  // Buscar informações detalhadas de pontos do cliente (incluindo expiração)
  async getCustomerPointsDetails(customerId, companyId) {
    try {
      const customer = await this.getById(customerId);
      if (!customer) {
        throw new Error('Cliente não encontrado');
      }

      // Para consulta pública, retornar informações básicas sem acessar a coleção de pontos
      try {
        // Importar pointsService dinamicamente para evitar circular dependency
        const { pointsService } = await import('./pointsService');
        
        // Tentar buscar pontos válidos e informações de expiração
        const pointsInfo = await pointsService.getValidCustomerPoints(customerId, companyId);
        
        // Buscar pontos expirados
        const expiredPoints = await pointsService.getExpiredPoints(customerId, companyId);
        
        return {
          customer,
          currentPoints: customer.points || 0,
          validPoints: pointsInfo.validPoints,
          pointsToExpire: pointsInfo.pointsToExpire,
          expiredPoints: expiredPoints,
          hasExpirationInfo: true
        };
      } catch (authError) {
        // Se não conseguir acessar por falta de permissão, retornar informações básicas
        if (authError.message.includes('permissions') || authError.message.includes('Permission')) {
          return {
            customer,
            currentPoints: customer.points || 0,
            validPoints: customer.points || 0,
            pointsToExpire: [],
            expiredPoints: [],
            hasExpirationInfo: false,
            error: 'Informações detalhadas de expiração não disponíveis na consulta pública'
          };
        }
        throw authError;
      }
    } catch (error) {
      throw error;
    }
  },

  // Gerar código de indicação para cliente existente
  async addReferralCodeToCustomer(customerId) {
    try {
      // Buscar o cliente primeiro
      const customer = await this.getById(customerId);
      
      if (customer.referralCode) {
        throw new Error('Cliente já possui código de indicação');
      }

      // Gerar novo código
      const referralCode = this.generateReferralCode(customer.name, customer.cpf);

      // Atualizar o cliente
      const customerRef = doc(db, 'customers', customerId);
      await updateDoc(customerRef, {
        referralCode,
        updatedAt: Timestamp.now()
      });

      return referralCode;
    } catch (error) {
      throw error;
    }
  },

  // Buscar resgates do cliente
  async getRedemptions(customerId) {
    try {
      const redemptionsRef = collection(db, 'redemptions');
      const q = query(
        redemptionsRef,
        where('customerId', '==', customerId),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Erro ao buscar resgates:', error);
      return [];
    }
  },

  // Buscar dados da empresa
  async getCompanyData(companyId) {
    try {
      const companyDoc = await getDoc(doc(db, 'companies', companyId));
      
      if (companyDoc.exists()) {
        return { id: companyDoc.id, ...companyDoc.data() };
      }
      
      return null;
    } catch (error) {
      console.error('Erro ao buscar dados da empresa:', error);
      return null;
    }
  }
};